import React, { useEffect, useRef, useState } from 'react';
import {
  standardCupcakeFlavors,
  specialtyCupcakeFlavors,
  syncCupcakeUnitConfigs,
  migrateLegacyCupcakeConfigs,
  isSingleDozenOrder,
  isUnitConfigured,
  CUPCAKE_CONFIG_MODES,
  getCupcakeConfigModeOptions,
  splitUnitsIntoHalves,
  applyTemplateToUnits,
  halfBlockAllowsHalfAndHalf,
  isExactWholeTwoDozenOrder,
} from '../utils/cupcakeOrderUtils';

const chipBase =
  'flex w-full items-start gap-2.5 rounded-lg border px-3 py-2.5 min-h-[44px] text-left text-xs sm:text-sm cursor-pointer touch-manipulation transition-colors';
const chipSelected = 'border-candy-primary bg-candy-background';
const chipDefault = 'border-candy-border bg-white hover:border-candy-primary/60';

function UnitFlavorPicker({ unit, onUpdate, allowHalfAndHalf }) {
  const selectFlavor = (flavor) => {
    if (unit.halfAndHalf) {
      if (unit.flavors.includes(flavor)) {
        onUpdate({ flavors: unit.flavors.filter((f) => f !== flavor) });
      } else if (unit.flavors.length < 2) {
        onUpdate({ flavors: [...unit.flavors, flavor] });
      }
      return;
    }
    onUpdate({ flavors: [flavor] });
  };

  const setHalfAndHalf = (enabled) => {
    onUpdate({
      halfAndHalf: enabled,
      flavors: enabled ? unit.flavors.slice(0, 2) : unit.flavors.slice(0, 1),
    });
  };

  const renderFlavor = (flavor, isSpecialty) => (
    <label
      key={flavor}
      className={`${chipBase} ${
        unit.flavors.includes(flavor) ? chipSelected : chipDefault
      }`}
    >
      <input
        type={unit.halfAndHalf ? 'checkbox' : 'radio'}
        name={`unit-${unit.unitIndex}-flavor`}
        checked={unit.flavors.includes(flavor)}
        onChange={() => selectFlavor(flavor)}
        className="accent-candy-primary mt-0.5 shrink-0"
      />
      <span className="min-w-0 flex-1 leading-snug break-words">{flavor}</span>
      {isSpecialty && (
        <span className="shrink-0 text-[10px] text-gray-500 self-center">specialty</span>
      )}
    </label>
  );

  return (
    <div className="space-y-3 min-w-0">
      {allowHalfAndHalf && (
        <label className={`${chipBase} border-amber-200 bg-amber-50 font-medium text-amber-950 items-center`}>
          <input
            type="checkbox"
            checked={unit.halfAndHalf}
            onChange={(e) => setHalfAndHalf(e.target.checked)}
            className="accent-candy-primary shrink-0"
          />
          <span className="min-w-0 flex-1 leading-snug">
            Half & half (two flavors for this {unit.size === 1 ? 'dozen' : 'order'})
          </span>
        </label>
      )}

      <p className="text-xs sm:text-sm text-gray-600 leading-snug">
        {unit.halfAndHalf
          ? `Select exactly 2 flavors (${unit.flavors.length}/2).`
          : unit.size === 0.5
            ? 'Select one flavor for this half dozen.'
            : 'Select one flavor for this dozen.'}
      </p>

      <div className="min-w-0">
        <p className="text-xs font-semibold text-gray-600 mb-2">Standard (no extra cost)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {standardCupcakeFlavors.map((f) => renderFlavor(f, false))}
        </div>
      </div>
      <div className="min-w-0">
        <p className="text-xs sm:text-sm font-semibold text-gray-600 mb-2 leading-snug">
          Specialty (+$5 per flavor up to 4 dozen, +$10 per flavor from 5 dozen)
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {specialtyCupcakeFlavors.map((f) => renderFlavor(f, true))}
        </div>
      </div>
    </div>
  );
}

function UnitIcingSection({ unit, onUpdate, buttercreamTintColorOptions }) {
  const icingSnapshotBeforeBlackRef = useRef(null);

  const toggleIcing = (name) => {
    if (name === 'White Buttercream') {
      icingSnapshotBeforeBlackRef.current = null;
      onUpdate({
        icingSelections: ['White Buttercream'],
        ...(unit.icingTintColor === 'Black'
          ? { icingTintColor: '', icingTintEnabled: false }
          : {}),
      });
      return;
    }
    icingSnapshotBeforeBlackRef.current = null;
    onUpdate({
      icingSelections: [name],
      icingTintColor: '',
      icingTintEnabled: false,
    });
  };

  const setTintEnabled = (enabled) => {
    if (!enabled) {
      if (unit.icingTintColor === 'Black' && icingSnapshotBeforeBlackRef.current != null) {
        onUpdate({
          icingSelections: [...icingSnapshotBeforeBlackRef.current],
          icingTintEnabled: false,
          icingTintColor: '',
        });
        icingSnapshotBeforeBlackRef.current = null;
        return;
      }
      onUpdate({ icingTintEnabled: false, icingTintColor: '' });
      return;
    }
    onUpdate({ icingTintEnabled: true });
  };

  const handleTintColorChange = (color) => {
    if (color === 'Black') {
      if (icingSnapshotBeforeBlackRef.current === null) {
        icingSnapshotBeforeBlackRef.current = [...unit.icingSelections];
      }
      onUpdate({
        icingSelections: ['Chocolate Buttercream'],
        icingTintColor: 'Black',
        icingTintEnabled: true,
      });
      return;
    }
    if (unit.icingTintColor === 'Black' && icingSnapshotBeforeBlackRef.current != null) {
      onUpdate({
        icingSelections: [...icingSnapshotBeforeBlackRef.current],
        icingTintColor: color,
        icingTintEnabled: true,
      });
      icingSnapshotBeforeBlackRef.current = null;
      return;
    }
    onUpdate({ icingTintColor: color, icingTintEnabled: true });
  };

  const icingNames = ['White Buttercream', 'Cream Cheese', 'Chocolate Buttercream', 'German Chocolate Icing'];
  const hasWhiteButtercream = unit.icingSelections.includes('White Buttercream');
  const canTintIcing = hasWhiteButtercream;
  const showTintColors =
    unit.icingTintEnabled &&
    (hasWhiteButtercream || unit.icingTintColor === 'Black');

  return (
    <div className="space-y-3 min-w-0">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
        {icingNames.map((name) => (
          <label
            key={name}
            className={`${chipBase} items-center ${
              unit.icingSelections.includes(name) ? chipSelected : chipDefault
            }`}
          >
            <input
              type="radio"
              name={`unit-${unit.unitIndex}-icing`}
              checked={unit.icingSelections.includes(name)}
              onChange={() => toggleIcing(name)}
              className="accent-candy-primary shrink-0"
            />
            <span className="min-w-0 flex-1 leading-snug break-words">{name}</span>
          </label>
        ))}
      </div>

      <div className="space-y-1 min-w-0">
        <label
          className={`${chipBase} items-center font-medium ${
            canTintIcing ? 'text-gray-800 cursor-pointer' : 'text-gray-400 cursor-not-allowed opacity-70'
          } ${canTintIcing ? chipDefault : 'border-gray-200 bg-gray-50'}`}
        >
          <input
            type="checkbox"
            checked={canTintIcing && unit.icingTintEnabled}
            disabled={!canTintIcing}
            onChange={(e) => canTintIcing && setTintEnabled(e.target.checked)}
            className="accent-candy-primary shrink-0 disabled:opacity-50"
          />
          <span className="min-w-0 flex-1 leading-snug">Tint icing</span>
        </label>
        {!canTintIcing && (
          <p className="text-xs text-gray-500 pl-1 leading-snug">
            Only available for white buttercream
          </p>
        )}
      </div>

      {showTintColors && (
        <div className="rounded-lg border border-candy-border bg-[#fef9f9] p-3 min-w-0">
          <p className="text-xs sm:text-sm text-gray-600 mb-2 leading-snug">
            Tint (+$5 per color up to 4 dozen, +$10 per color from 5 dozen — counted globally per color)
          </p>
          {unit.icingTintColor === 'Black' && (
            <p className="mb-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 leading-snug">
              Black tint uses chocolate buttercream only.
            </p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {buttercreamTintColorOptions.map((color) => (
              <label
                key={color}
                className={`${chipBase} items-center min-h-[40px] py-2 ${
                  unit.icingTintColor === color ? chipSelected : chipDefault
                }`}
              >
                <input
                  type="radio"
                  name={`unit-${unit.unitIndex}-tint`}
                  checked={unit.icingTintColor === color}
                  onChange={() => handleTintColorChange(color)}
                  className="accent-candy-primary shrink-0"
                />
                <span className="min-w-0 flex-1 leading-snug">{color}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UnitSprinkleSection({ unit, onUpdate, sugarSprinkleColorOptions }) {
  const toggle = (color) => {
    const prev = unit.sugarSprinkleColors || [];
    if (prev.includes(color)) {
      onUpdate({ sugarSprinkleColors: prev.filter((c) => c !== color) });
    } else if (prev.length < 2) {
      onUpdate({ sugarSprinkleColors: [...prev, color] });
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 min-w-0">
      {sugarSprinkleColorOptions.map((color) => (
        <label
          key={color}
          className={`${chipBase} items-center ${
            unit.sugarSprinkleColors?.includes(color) ? chipSelected : chipDefault
          }`}
        >
          <input
            type="checkbox"
            checked={unit.sugarSprinkleColors?.includes(color)}
            onChange={() => toggle(color)}
            disabled={
              !unit.sugarSprinkleColors?.includes(color) &&
              (unit.sugarSprinkleColors?.length || 0) >= 2
            }
            className="accent-candy-primary shrink-0"
          />
          <span className="min-w-0 flex-1 leading-snug">{color}</span>
        </label>
      ))}
    </div>
  );
}

function UnitConfigEditor({
  title,
  subtitle,
  unit,
  onUpdate,
  allowHalfAndHalf,
  buttercreamTintColorOptions,
  sugarSprinkleColorOptions,
}) {
  return (
    <div className="rounded-xl border border-candy-border bg-white p-3 sm:p-4 md:p-5 space-y-4 sm:space-y-5 min-w-0 overflow-hidden">
      <div className="min-w-0">
        <h3 className="text-base sm:text-lg font-semibold text-gray-800 leading-snug break-words">{title}</h3>
        {subtitle && <p className="text-xs sm:text-sm text-gray-600 mt-1 leading-snug">{subtitle}</p>}
      </div>
      <div className="min-w-0">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Flavors</h4>
        <UnitFlavorPicker unit={unit} onUpdate={onUpdate} allowHalfAndHalf={allowHalfAndHalf} />
      </div>
      <div className="min-w-0">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Icing</h4>
        <UnitIcingSection
          unit={unit}
          onUpdate={onUpdate}
          buttercreamTintColorOptions={buttercreamTintColorOptions}
        />
      </div>
      <div className="min-w-0">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">
          Sugar sprinkles <span className="font-normal text-gray-500">(optional, up to 2 colors)</span>
        </h4>
        <UnitSprinkleSection
          unit={unit}
          onUpdate={onUpdate}
          sugarSprinkleColorOptions={sugarSprinkleColorOptions}
        />
      </div>
    </div>
  );
}

function ConfigModeSelector({ value, onChange, totalDozens }) {
  const options = getCupcakeConfigModeOptions(totalDozens);

  return (
    <div className="rounded-xl border border-candy-primary/20 bg-[#fef2f2] p-3 sm:p-4 space-y-3 min-w-0">
      <div className="min-w-0">
        <p className="text-sm sm:text-base font-semibold text-gray-800 leading-snug">
          How would you like to configure your cupcakes?
        </p>
        <p className="text-xs sm:text-sm text-gray-600 mt-1">
          {totalDozens} dozen ({Math.round(totalDozens * 12)} cupcakes)
        </p>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {options.map((option) => {
          const selected = value === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange(option.id)}
              className={`w-full rounded-lg border-2 p-3 sm:p-4 text-left transition min-h-[44px] touch-manipulation ${
                selected
                  ? 'border-candy-primary bg-white shadow-sm'
                  : 'border-candy-border bg-white/80 hover:border-candy-primary/50 active:bg-red-50'
              }`}
            >
              <p className="font-semibold text-gray-800 text-sm sm:text-base leading-snug">{option.title}</p>
              <p className="text-xs sm:text-sm text-gray-600 mt-1.5 leading-snug">{option.description}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CupcakeUnitCard({
  unit,
  onUpdate,
  expanded,
  onToggleExpand,
  showHalfAndHalf,
  buttercreamTintColorOptions,
  sugarSprinkleColorOptions,
  copyFromPrevious,
  showCopy,
}) {
  const configured = isUnitConfigured(unit);

  return (
    <div
      className={`rounded-xl border-2 overflow-hidden transition-shadow min-w-0 ${
        configured ? 'border-candy-primary/40 bg-white' : 'border-candy-border bg-gray-50/80'
      }`}
    >
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-4 py-3 min-h-[56px] text-left bg-white hover:bg-[#fef9f9] transition touch-manipulation"
      >
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <span
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
              configured ? 'bg-candy-primary text-white' : 'bg-gray-200 text-gray-600'
            }`}
          >
            {unit.unitIndex}
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-gray-800 text-sm sm:text-base">{unit.label}</p>
            <p className="text-xs text-gray-500 line-clamp-2 sm:truncate leading-snug">
              {unit.size === 0.5 ? '6 cupcakes' : '12 cupcakes'}
              {unit.flavors?.length ? ` · ${unit.flavors.join(' / ')}` : ' · not configured'}
            </p>
          </div>
        </div>
        <span className="text-gray-400 text-lg shrink-0 pl-1" aria-hidden="true">
          {expanded ? '▾' : '▸'}
        </span>
      </button>

      {expanded && (
        <div className="px-3 sm:px-4 pb-4 pt-1 space-y-4 sm:space-y-5 border-t border-candy-border/60 bg-white min-w-0">
          {showCopy && (
            <button
              type="button"
              onClick={copyFromPrevious}
              className="text-sm font-medium text-candy-primary underline hover:no-underline touch-manipulation"
            >
              Copy options from previous {unit.unitIndex === 2 ? 'dozen' : 'unit'}
            </button>
          )}

          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Flavors</h4>
            <UnitFlavorPicker unit={unit} onUpdate={onUpdate} allowHalfAndHalf={showHalfAndHalf} />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Icing</h4>
            <UnitIcingSection
              unit={unit}
              onUpdate={onUpdate}
              buttercreamTintColorOptions={buttercreamTintColorOptions}
            />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">
          Sugar sprinkles <span className="font-normal text-gray-500">(optional, up to 2 colors)</span>
        </h4>
            <UnitSprinkleSection
              unit={unit}
              onUpdate={onUpdate}
              sugarSprinkleColorOptions={sugarSprinkleColorOptions}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function CupcakeOptionsPanel({
  totalDozens,
  initialConfigs,
  formData,
  onConfigsChange,
  sugarSprinkleColorOptions,
  buttercreamTintColorOptions,
}) {
  const [configMode, setConfigMode] = useState(
    () => formData.cupcakeConfigMode || CUPCAKE_CONFIG_MODES.UNIFORM
  );
  const [unitConfigs, setUnitConfigs] = useState(() =>
    migrateLegacyCupcakeConfigs({ ...formData, cupcakeUnitConfigs: initialConfigs })
  );
  const [expandedIndex, setExpandedIndex] = useState(1);

  useEffect(() => {
    setUnitConfigs((prev) => syncCupcakeUnitConfigs(prev, totalDozens, configMode));
  }, [totalDozens, configMode]);

  useEffect(() => {
    onConfigsChange({ unitConfigs, configMode });
  }, [unitConfigs, configMode, onConfigsChange]);

  useEffect(() => {
    if (isSingleDozenOrder(totalDozens, unitConfigs) && configMode === CUPCAKE_CONFIG_MODES.BY_DOZEN) {
      setConfigMode(CUPCAKE_CONFIG_MODES.UNIFORM);
    }
  }, [totalDozens, unitConfigs, configMode]);

  const handleModeChange = (mode) => {
    setConfigMode(mode);
    setUnitConfigs((prev) => syncCupcakeUnitConfigs(prev, totalDozens, mode));
  };

  const updateUnit = (unitIndex, patch) => {
    setUnitConfigs((prev) =>
      prev.map((u) => (u.unitIndex === unitIndex ? { ...u, ...patch } : u))
    );
  };

  const updateUniform = (patch) => {
    setUnitConfigs((prev) => {
      const template = { ...prev[0], ...patch };
      return applyTemplateToUnits(prev, template);
    });
  };

  const updateHalfBlock = (blockUnits, patch) => {
    setUnitConfigs((prev) => {
      const blockIndices = new Set(blockUnits.map((u) => u.unitIndex));
      const template = { ...(blockUnits[0] || prev[0]), ...patch };
      return prev.map((unit) =>
        blockIndices.has(unit.unitIndex) ? applyTemplateToUnits([unit], template)[0] : unit
      );
    });
  };

  const singleDozen = isSingleDozenOrder(totalDozens, unitConfigs);
  const twoDozenHalfAndHalf =
    configMode === CUPCAKE_CONFIG_MODES.HALF_AND_HALF && isExactWholeTwoDozenOrder(totalDozens);
  const configuredCount = unitConfigs.filter(isUnitConfigured).length;
  const { firstHalf, secondHalf } = splitUnitsIntoHalves(unitConfigs);
  const firstHalfDozens = firstHalf.reduce((sum, unit) => sum + unit.size, 0);
  const secondHalfDozens = secondHalf.reduce((sum, unit) => sum + unit.size, 0);

  if (!totalDozens) {
    return (
      <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 sm:px-4 py-3">
        Go back and select your cupcake quantity first.
      </p>
    );
  }

  const panelShell = (children) => (
    <div className="w-full min-w-0 max-w-full overflow-x-hidden space-y-3 sm:space-y-4">{children}</div>
  );

  if (singleDozen && unitConfigs[0]) {
    const unit = unitConfigs[0];
    return panelShell(
      <>
        <ConfigModeSelector value={configMode} onChange={handleModeChange} totalDozens={totalDozens} />
        {configMode === CUPCAKE_CONFIG_MODES.UNIFORM && (
          <UnitConfigEditor
            title="All cupcakes — same configuration"
            subtitle="One flavor, icing, and sprinkles for all 12 cupcakes."
            unit={unit}
            onUpdate={(patch) => updateUnit(1, patch)}
            allowHalfAndHalf={false}
            buttercreamTintColorOptions={buttercreamTintColorOptions}
            sugarSprinkleColorOptions={sugarSprinkleColorOptions}
          />
        )}
        {configMode === CUPCAKE_CONFIG_MODES.HALF_AND_HALF && (
          <UnitConfigEditor
            title="Half and half customization"
            subtitle="Choose two flavors — six cupcakes of each."
            unit={unit}
            onUpdate={(patch) => updateUnit(1, patch)}
            allowHalfAndHalf
            buttercreamTintColorOptions={buttercreamTintColorOptions}
            sugarSprinkleColorOptions={sugarSprinkleColorOptions}
          />
        )}
      </>
    );
  }

  return panelShell(
    <>
      <ConfigModeSelector value={configMode} onChange={handleModeChange} totalDozens={totalDozens} />

      {configMode === CUPCAKE_CONFIG_MODES.UNIFORM && unitConfigs[0] && (
        <UnitConfigEditor
          title="All cupcakes — same configuration"
          subtitle="Your choices below apply to every dozen in this order."
          unit={unitConfigs[0]}
          onUpdate={updateUniform}
          allowHalfAndHalf={false}
          buttercreamTintColorOptions={buttercreamTintColorOptions}
          sugarSprinkleColorOptions={sugarSprinkleColorOptions}
        />
      )}

      {configMode === CUPCAKE_CONFIG_MODES.HALF_AND_HALF && twoDozenHalfAndHalf && unitConfigs[0] && unitConfigs[1] && (
        <div className="space-y-3 sm:space-y-4 min-w-0">
          <UnitConfigEditor
            title={unitConfigs[0].label || 'Dozen 1'}
            subtitle="One flavor, icing, and sprinkles for all 12 cupcakes in this dozen."
            unit={unitConfigs[0]}
            onUpdate={(patch) => updateUnit(1, patch)}
            allowHalfAndHalf={false}
            buttercreamTintColorOptions={buttercreamTintColorOptions}
            sugarSprinkleColorOptions={sugarSprinkleColorOptions}
          />
          <UnitConfigEditor
            title={unitConfigs[1].label || 'Dozen 2'}
            subtitle="One flavor, icing, and sprinkles for all 12 cupcakes in this dozen."
            unit={unitConfigs[1]}
            onUpdate={(patch) => updateUnit(2, patch)}
            allowHalfAndHalf={false}
            buttercreamTintColorOptions={buttercreamTintColorOptions}
            sugarSprinkleColorOptions={sugarSprinkleColorOptions}
          />
        </div>
      )}

      {configMode === CUPCAKE_CONFIG_MODES.HALF_AND_HALF && !twoDozenHalfAndHalf && (
        <div className="space-y-3 sm:space-y-4 min-w-0">
          <UnitConfigEditor
            title={firstHalf[0]?.label || `First half (${firstHalfDozens} dozen)`}
            subtitle={`Configure the first ${Math.round(firstHalfDozens * 12)} cupcakes — one flavor for this block.`}
            unit={firstHalf[0] || unitConfigs[0]}
            onUpdate={(patch) => updateHalfBlock(firstHalf, patch)}
            allowHalfAndHalf={halfBlockAllowsHalfAndHalf(firstHalf, totalDozens)}
            buttercreamTintColorOptions={buttercreamTintColorOptions}
            sugarSprinkleColorOptions={sugarSprinkleColorOptions}
          />
          <UnitConfigEditor
            title={secondHalf[0]?.label || `Second half (${secondHalfDozens} dozen)`}
            subtitle={`Configure the remaining ${Math.round(secondHalfDozens * 12)} cupcakes — one flavor for this block.`}
            unit={secondHalf[0] || unitConfigs[unitConfigs.length - 1]}
            onUpdate={(patch) => updateHalfBlock(secondHalf, patch)}
            allowHalfAndHalf={halfBlockAllowsHalfAndHalf(secondHalf, totalDozens)}
            buttercreamTintColorOptions={buttercreamTintColorOptions}
            sugarSprinkleColorOptions={sugarSprinkleColorOptions}
          />
        </div>
      )}

      {configMode === CUPCAKE_CONFIG_MODES.BY_DOZEN && (
        <>
          <div className="rounded-lg bg-[#fef2f2] border border-candy-primary/20 px-3 sm:px-4 py-3 min-w-0">
            <p className="text-sm font-semibold text-gray-800">Customize by dozen</p>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 leading-snug">
              Set flavors, icing, and sprinkles for each{' '}
              {unitConfigs.some((u) => u.size === 0.5) ? 'dozen or half dozen' : 'dozen'} below.
              One flavor per unit.
            </p>
            <div className="mt-2 h-2 rounded-full bg-gray-200 overflow-hidden">
              <div
                className="h-full bg-candy-primary transition-all duration-300"
                style={{ width: `${(configuredCount / unitConfigs.length) * 100}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {configuredCount} of {unitConfigs.length} units configured
            </p>
          </div>

          <div className="space-y-2 sm:space-y-3 min-w-0">
            {unitConfigs.map((unit) => (
              <CupcakeUnitCard
                key={unit.unitIndex}
                unit={unit}
                expanded={expandedIndex === unit.unitIndex}
                onToggleExpand={() =>
                  setExpandedIndex((prev) => (prev === unit.unitIndex ? null : unit.unitIndex))
                }
                onUpdate={(patch) => updateUnit(unit.unitIndex, patch)}
                showHalfAndHalf={false}
                buttercreamTintColorOptions={buttercreamTintColorOptions}
                sugarSprinkleColorOptions={sugarSprinkleColorOptions}
                showCopy={unit.unitIndex > 1}
                copyFromPrevious={() => {
                  const prev = unitConfigs.find((u) => u.unitIndex === unit.unitIndex - 1);
                  if (!prev) return;
                  updateUnit(unit.unitIndex, {
                    halfAndHalf: false,
                    flavors: prev.flavors.slice(0, 1),
                    icingSelections: [...prev.icingSelections],
                    icingTintEnabled: prev.icingTintEnabled,
                    icingTintColor: prev.icingTintColor,
                    sugarSprinkleColors: [...prev.sugarSprinkleColors],
                  });
                }}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
}
